import {
  CmpAdvertisingOptionFieldsFragment,
  CmpInvoiceCategoryDefaultsFieldsFragment,
} from "../../../../graphql/types";
import { MockInvoiceLineItem } from "../mockInvoiceData";

// Pure helpers for the Create Invoice page — kept side-effect-free so the
// catalog→line-item mapping and the chamber-setup-driven tax/account defaults
// can be unit tested in isolation.

export type InvoiceTab = "custom" | "advertising";

export type CategoryDefaults = Pick<
  CmpInvoiceCategoryDefaultsFieldsFragment,
  "accountNumber" | "accountName" | "taxRateLabel" | "taxRatePercentage" | "taxEnabled"
>;

export type LineItemTaxOption = {
  label: string;
  ratePercentage: number;
};

// Sentinel option id for a free-entry ("Custom") line item.
export const CUSTOM_LINE_OPTION_ID = "__custom__";

let lineItemIdCounter = 0;

export const makeLineItemId = () => {
  lineItemIdCounter += 1;
  return `line-${Date.now()}-${lineItemIdCounter}-${Math.round(
    Math.random() * 1_000_000
  )}`;
};

export const formatTaxOptionLabel = (label: string, ratePercentage: number) =>
  `${(label || "").trim() || "Tax"} (${ratePercentage}%)`;

// TICK-10 (B5): the totals row and email preview show the same "Tax (rate%)"
// shape as the tax dropdown. Defensive guard: a backend label that already
// carries a "(...)" suffix (or a zero/unset rate) renders as-is so the suffix
// is never doubled.
export const formatTaxSummaryLabel = (
  label?: string | null,
  ratePercentage?: number | null
) => {
  const trimmedLabel = (label || "").trim() || "Tax";

  if (trimmedLabel.includes("(") || !ratePercentage || ratePercentage <= 0) {
    return trimmedLabel;
  }

  return formatTaxOptionLabel(trimmedLabel, ratePercentage);
};

// The TAX dropdown offers the chamber's configured rate label plus
// "Tax Exempt" (0%). Entirely sourced from chamber setup.
export const getLineItemTaxOptions = (
  defaults?: Pick<
    CategoryDefaults,
    "taxRateLabel" | "taxRatePercentage"
  > | null
): LineItemTaxOption[] => {
  const ratePercentage = defaults?.taxRatePercentage || 0;
  const options: LineItemTaxOption[] = [];

  if (ratePercentage > 0) {
    options.push({
      label: formatTaxOptionLabel(defaults?.taxRateLabel || "Tax", ratePercentage),
      ratePercentage,
    });
  }

  options.push({ label: "Tax Exempt", ratePercentage: 0 });

  return options;
};

export const getCategoryDefaults = (
  invoiceDefaults:
    | {
        advertising: CategoryDefaults;
        custom: CategoryDefaults;
      }
    | null
    | undefined,
  tab: InvoiceTab
): CategoryDefaults | null => {
  if (!invoiceDefaults) {
    return null;
  }

  return tab === "advertising" ? invoiceDefaults.advertising : invoiceDefaults.custom;
};

export const createEmptyLineItem = ({
  accountNumber,
  defaultTaxRatePercentage,
}: {
  accountNumber?: string;
  defaultTaxRatePercentage?: number;
}): MockInvoiceLineItem => ({
  id: makeLineItemId(),
  code: "",
  catalogOptionId: "",
  accountNumber: accountNumber || "",
  description: "",
  quantity: 1,
  unitPriceInCents: 0,
  taxRate: defaultTaxRatePercentage || 0,
});

// Selecting a catalog option fills code / description / unit price. "Custom"
// clears the catalog link and lets code + description be free-text.
export const applyCatalogOptionToLineItem = ({
  lineItem,
  optionId,
  options,
}: {
  lineItem: MockInvoiceLineItem;
  optionId: string;
  options: CmpAdvertisingOptionFieldsFragment[];
}): MockInvoiceLineItem => {
  if (optionId === CUSTOM_LINE_OPTION_ID) {
    return { ...lineItem, catalogOptionId: CUSTOM_LINE_OPTION_ID };
  }

  if (!optionId) {
    return {
      ...lineItem,
      catalogOptionId: "",
      code: "",
      description: "",
      unitPriceInCents: 0,
    };
  }

  const option = options.find((candidate) => candidate.id === optionId);

  if (!option) {
    return lineItem;
  }

  return {
    ...lineItem,
    catalogOptionId: option.id,
    code: option.code,
    description: option.name,
    unitPriceInCents: option.priceInCents,
  };
};

export const isCustomLineItem = (lineItem: MockInvoiceLineItem) =>
  lineItem.catalogOptionId === CUSTOM_LINE_OPTION_ID;

export const isValidInvoiceEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export const isPristineEmptyLineItem = (lineItem: MockInvoiceLineItem) =>
  !lineItem.code &&
  !lineItem.catalogOptionId &&
  !lineItem.accountNumber &&
  !lineItem.description &&
  lineItem.quantity === 1 &&
  lineItem.unitPriceInCents === 0 &&
  lineItem.taxRate === 0;

export const parseCurrencyToCents = (value: string) => {
  // A negative input is never a valid price/amount — clamp to 0 rather than
  // silently flipping the sign by stripping the leading "-".
  if (value.trim().startsWith("-")) {
    return 0;
  }

  return Math.max(0, Math.round((Number(value.replace(/[^0-9.]/g, "")) || 0) * 100));
};
